import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, NavigateNext, Refresh, Save, Cancel } from '@mui/icons-material';
import { FormNavigationProvider, NavTextField, NavSelect } from '../components/FormNavigation';
import DashboardLayout from '../components/DashboardLayout';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchShiftGroups,
  upsertShiftGroup,
  removeShiftGroupsByGroupId,
  removeShiftGroupEditPoint,
} from '../store/shiftGroupsSlice';
import { fetchShifts } from '../store/shiftsSlice';
import { showNotification } from '../store/notificationSlice';
import { apiService } from '../services/api';
import type { ShiftGroup, ShiftGroupPayload } from '../types/shiftGroups';

// ─── Constants ────────────────────────────────────────────────────────────────

const ITEM_HEIGHT = 56;
const TIMELINE_HEIGHT = 440;

function emptyPayload(): ShiftGroupPayload {
  return {
    editPointDate: new Date().toISOString().split('T')[0],
    shiftGroupName: '',
    shiftIdList: [],
  };
}

function groupToPayload(g: ShiftGroup): ShiftGroupPayload {
  return {
    shiftGroupId: g.shiftGroupId,
    editPointDate: g.editPointDate,
    shiftGroupName: g.shiftGroupName,
    shiftIdList: [...g.shiftIdList],
  };
}

// ─── ShiftGroupFormFields ─────────────────────────────────────────────────────

interface FormFieldsProps {
  form: ShiftGroupPayload;
  errors: Record<string, string>;
  readOnly?: boolean;
  lockGroupName?: boolean;
  hideEditPointDate?: boolean;
  availableShifts: Array<{ shiftId: string; shiftName: string }>;
  onChange: (updated: ShiftGroupPayload) => void;
}

function ShiftGroupFormFields({ form, errors, readOnly, lockGroupName, hideEditPointDate, availableShifts, onChange }: FormFieldsProps) {
  const handleChange = (field: keyof ShiftGroupPayload, value: any) => {
    onChange({ ...form, [field]: value });
  };

  const handleShiftIDListChange = (event: any) => {
    const value = event.target.value;
    onChange({ ...form, shiftIdList: typeof value === 'string' ? value.split(',') : value });
  };

  const shiftLabelMap = Object.fromEntries(availableShifts.map((s) => [s.shiftId, s.shiftName]));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      {!hideEditPointDate && (
        <NavTextField
          navFieldType="date"
          label="Version Date"
          type="date"
          value={form.editPointDate}
          onChange={(e) => handleChange('editPointDate', e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={!!errors['editPointDate']}
          helperText={errors['editPointDate']}
          disabled={readOnly}
          fullWidth
        />
      )}
      <NavTextField
        label="Shift Group Name"
        value={form.shiftGroupName}
        onChange={(e) => handleChange('shiftGroupName', e.target.value)}
        error={!!errors['shiftGroupName']}
        helperText={errors['shiftGroupName']}
        disabled={readOnly || lockGroupName}
        fullWidth
      />

      <FormControl fullWidth error={!!errors['shiftIdList']} disabled={readOnly}>
        <InputLabel shrink>Shifts</InputLabel>
        <NavSelect
          multiple
          value={form.shiftIdList}
          onChange={handleShiftIDListChange}
          input={<OutlinedInput notched label="Shifts" />}
          renderValue={(selected: any) =>
            selected.length === 0 ? (
              <Typography color="text.secondary">No shifts selected</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id: string) => (
                  <Chip key={id} label={shiftLabelMap[id] ?? id} size="small" />
                ))}
              </Box>
            )
          }
        >
          {availableShifts.map((s) => (
            <MenuItem key={s.shiftId} value={s.shiftId}>
              <Checkbox checked={form.shiftIdList.includes(s.shiftId)} />
              <ListItemText primary={s.shiftName} secondary={s.shiftId} />
            </MenuItem>
          ))}
        </NavSelect>
        {errors['shiftIdList'] && (
          <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
            {errors['shiftIdList']}
          </Typography>
        )}
        {Object.entries(errors)
          .filter(([k]) => k.startsWith('shiftIdList['))
          .map(([k, v]) => (
            <Typography key={k} variant="caption" color="error" sx={{ display: 'block', ml: 1.5 }}>
              {v}
            </Typography>
          ))}
      </FormControl>

    </Box>
  );
}

// ─── CreateShiftGroupDialog ───────────────────────────────────────────────────

interface CreateDialogProps {
  open: boolean;
  availableShifts: Array<{ shiftId: string; shiftName: string }>;
  onClose: () => void;
  onCreated: (sg: ShiftGroup) => void;
}

function CreateShiftGroupDialog({ open, availableShifts, onClose, onCreated }: CreateDialogProps) {
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<ShiftGroupPayload>(emptyPayload());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      setForm(emptyPayload());
      setErrors({});
    }
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    try {
      const res = await apiService.createShiftGroup(form);
      onCreated(res.data);
      onClose();
    } catch (err: any) {
      if (err?.jsonMessage) {
        setErrors(err.jsonMessage);
      } else {
        dispatch(showNotification({ message: err?.message ?? 'Failed to create shift group', severity: 'error' }));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>Create Shift Group</DialogTitle>
      <DialogContent>
        <ShiftGroupFormFields
          form={form}
          errors={errors}
          availableShifts={availableShifts}
          onChange={setForm}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <Save />} disabled={saving}>
          Create
        </Button>
      </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
}

// ─── DeleteShiftGroupDialog ───────────────────────────────────────────────────

interface DeleteDialogProps {
  open: boolean;
  group: ShiftGroup | null;
  onClose: () => void;
  onDeleted: (shiftGroupId: string) => void;
}

function DeleteShiftGroupDialog({ open, group, onClose, onDeleted }: DeleteDialogProps) {
  const [deleting, setDeleting] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    if (open) setServerError('');
  }, [open]);

  const handleDelete = async () => {
    if (!group) return;
    setDeleting(true);
    setServerError('');
    try {
      await apiService.deleteShiftGroup(group.shiftGroupId);
      onDeleted(group.shiftGroupId);
      onClose();
    } catch (err: any) {
      setServerError(err?.message ?? 'Failed to delete shift group');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Shift Group</DialogTitle>
      <DialogContent>
        {serverError && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}
        <Typography>
          Delete all versions for <strong>{group?.shiftGroupName}</strong>? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}>
          Delete All
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── AddEditPointDialog ───────────────────────────────────────────────────────

interface AddEditPointDialogProps {
  open: boolean;
  shiftGroupId: string;
  availableShifts: Array<{ shiftId: string; shiftName: string }>;
  initialPayload?: ShiftGroupPayload;
  onClose: () => void;
  onAdded: (sg: ShiftGroup) => void;
}

function AddEditPointDialog({ open, shiftGroupId, availableShifts, initialPayload, onClose, onAdded }: AddEditPointDialogProps) {
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<ShiftGroupPayload>(emptyPayload());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initialPayload ? { ...initialPayload, editPointDate: '' } : emptyPayload());
      setErrors({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    try {
      const res = await apiService.createShiftGroupEditPoint(shiftGroupId, form);
      onAdded(res.data);
      onClose();
    } catch (err: any) {
      if (err?.jsonMessage) {
        setErrors(err.jsonMessage);
      } else {
        dispatch(showNotification({ message: err?.message ?? 'Failed to add version', severity: 'error' }));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>Add Version</DialogTitle>
      <DialogContent>
        <ShiftGroupFormFields
          form={form}
          errors={errors}
          lockGroupName={!!initialPayload}
          availableShifts={availableShifts}
          onChange={setForm}
        />
      </DialogContent>
      <DialogActions sx={{ p: '20px' }}>
        <Button onClick={onClose} startIcon={<Cancel />} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <Save />} disabled={saving}>
          Add Version
        </Button>
      </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
}

// ─── DeleteEditPointDialog ────────────────────────────────────────────────────

interface DeleteEditPointDialogProps {
  open: boolean;
  editPoint: ShiftGroup | null;
  onClose: () => void;
  onDeleted: (shiftGroupId: string, editPointDate: string) => void;
}

function DeleteEditPointDialog({ open, editPoint, onClose, onDeleted }: DeleteEditPointDialogProps) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) setError('');
  }, [open]);

  const handleDelete = async () => {
    if (!editPoint) return;
    setDeleting(true);
    setError('');
    try {
      await apiService.deleteShiftGroupEditPoint(editPoint.shiftGroupId, editPoint.editPointDate);
      onDeleted(editPoint.shiftGroupId, editPoint.editPointDate);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete version');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Version</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography>
          Delete version <strong>{editPoint?.editPointDate}</strong>? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── ShiftGroupDetailDialog ───────────────────────────────────────────────────

interface DetailDialogProps {
  open: boolean;
  shiftGroupId: string;
  editPoints: ShiftGroup[];
  availableShifts: Array<{ shiftId: string; shiftName: string }>;
  onClose: () => void;
  onUpdated: (sg: ShiftGroup) => void;
  onAdded: (sg: ShiftGroup) => void;
  onDeleted: (shiftGroupId: string, editPointDate: string) => void;
}

function ShiftGroupDetailDialog({
  open,
  shiftGroupId,
  editPoints: editPointsProp,
  availableShifts,
  onClose,
  onUpdated,
  onAdded,
  onDeleted,
}: DetailDialogProps) {
  const dispatch = useAppDispatch();
  const [localEditPoints, setLocalEditPoints] = useState<ShiftGroup[]>(editPointsProp);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Keep local state in sync when prop changes (e.g. after add/delete from parent)
  useEffect(() => { setLocalEditPoints(editPointsProp); }, [editPointsProp]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await apiService.listShiftGroupEditPoints(shiftGroupId);
      setLocalEditPoints(res.data ?? []);
    } catch {
      dispatch(showNotification({ message: 'Failed to refresh versions', severity: 'error' }));
    } finally {
      setRefreshing(false);
    }
  }, [shiftGroupId, dispatch]);
  const [form, setForm] = useState<ShiftGroupPayload>(emptyPayload());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ShiftGroup | null>(null);
  const [scrollOffset, setScrollOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  const sortedPoints = [...localEditPoints].sort((a, b) => a.editPointDate.localeCompare(b.editPointDate));
  const selectedPoint = sortedPoints.find((p) => p.editPointDate === selectedDate) ?? null;
  const groupName = sortedPoints[sortedPoints.length - 1]?.shiftGroupName ?? '';

  const maxScroll = Math.max(0, sortedPoints.length * ITEM_HEIGHT - TIMELINE_HEIGHT);
  const topOpacity = Math.min(1, scrollOffset / 40);
  const bottomOpacity = Math.min(1, (maxScroll - scrollOffset) / 40);

  useEffect(() => {
    if (open && sortedPoints.length > 0 && !selectedDate) {
      setSelectedDate(sortedPoints[sortedPoints.length - 1].editPointDate);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sortedPoints.length]);

  useEffect(() => {
    if (selectedPoint) {
      setForm(groupToPayload(selectedPoint));
      setErrors({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Scroll to selected item when selection changes (list is displayed newest-first)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const displayIdx = [...sortedPoints].reverse().findIndex((p) => p.editPointDate === selectedDate);
    if (displayIdx === -1) return;
    const targetCenter = displayIdx * ITEM_HEIGHT + ITEM_HEIGHT / 2;
    el.scrollTop = Math.max(0, targetCenter - TIMELINE_HEIGHT / 2);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, sortedPoints.length]);


  const handleSave = async () => {
    if (!selectedPoint) return;
    setSaving(true);
    setErrors({});
    try {
      const payload = {
        shiftGroupId: selectedPoint.shiftGroupId,
        editPointDate: selectedPoint.editPointDate,
        shiftGroupName: form.shiftGroupName,
        shiftIdList: form.shiftIdList,
      };
      const res = await apiService.updateShiftGroup(payload);
      onUpdated(res.data);
      setForm(groupToPayload(res.data));
      dispatch(showNotification({ message: 'Version updated', severity: 'success' }));
    } catch (err: any) {
      if (err?.jsonMessage) {
        setErrors(err.jsonMessage);
      } else {
        dispatch(showNotification({ message: err?.message ?? 'Failed to update version', severity: 'error' }));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEditPoint = (point: ShiftGroup) => {
    setDeleteTarget(point);
    setDeleteOpen(true);
  };

  const handleDeleteDone = (sgId: string, date: string) => {
    onDeleted(sgId, date);
    if (selectedDate === date) {
      const remaining = sortedPoints.filter((p) => p.editPointDate !== date);
      setSelectedDate(remaining.length > 0 ? remaining[remaining.length - 1].editPointDate : null);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ ref: navContainerRef }}
      >
        <FormNavigationProvider containerRef={navContainerRef}>
          <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">{groupName}</Typography>
            <Tooltip title="Refresh versions">
              <IconButton size="small" onClick={handleRefresh} disabled={refreshing}>
                {refreshing ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', height: TIMELINE_HEIGHT + 40, overflow: 'hidden' }}>
            {/* Left: timeline */}
            <Box sx={{ width: 220, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary' }}>
                  Versions
                </Typography>
                <Tooltip title="Add Version">
                  <IconButton size="small" onClick={() => setAddOpen(true)}>
                    <Add fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {/* Fades sit outside the scroll container so they don't scroll away */}
              <Box sx={{ position: 'relative', height: TIMELINE_HEIGHT }}>
                <Box
                  ref={containerRef}
                  onScroll={(e) => setScrollOffset(e.currentTarget.scrollTop)}
                  sx={{
                    height: '100%', overflowY: 'auto', userSelect: 'none',
                    '&::-webkit-scrollbar': { width: 5 },
                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#bdbdbd', borderRadius: 3 },
                    scrollbarWidth: 'thin',
                  }}
                >
                  {/* Upward arrow at top of timeline line */}
                  <Box sx={{
                    position: 'sticky', top: 0, left: 16, transform: 'translateX(-50%)',
                    width: 0, height: 0,
                    borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                    borderBottom: '8px solid', borderBottomColor: '#e0e0e0', zIndex: 0,
                  }} />
                  {sortedPoints.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No versions</Typography>
                  ) : [...sortedPoints].reverse().map((point) => {
                      const isSelected = point.editPointDate === selectedDate;
                      return (
                        <Box
                          key={point.editPointDate}
                          onClick={() => setSelectedDate(point.editPointDate)}
                          sx={{ display: 'flex', alignItems: 'center', height: ITEM_HEIGHT, cursor: 'pointer', '&:hover .date-label': { color: 'primary.main' } }}
                        >
                          {/* Line + dot */}
                          <Box sx={{ width: 32, position: 'relative', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, bgcolor: '#e0e0e0', transform: 'translateX(-50%)' }} />
                            <Box sx={{
                              width: isSelected ? 16 : 10, height: isSelected ? 16 : 10,
                              borderRadius: '50%', bgcolor: isSelected ? 'primary.main' : '#bdbdbd',
                              zIndex: 1, transition: 'all 0.15s',
                              border: isSelected ? '2px solid' : 'none', borderColor: 'primary.main',
                            }} />
                          </Box>
                          {/* Date + count */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              className="date-label"
                              variant="body2"
                              sx={{ fontWeight: isSelected ? 700 : 400, color: isSelected ? 'primary.main' : 'text.primary', transition: 'color 0.15s', fontSize: '0.82rem' }}
                            >
                              {point.editPointDate}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {point.shiftIdList.length} shift{point.shiftIdList.length !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                          {/* Delete */}
                          <Tooltip title="Delete this version">
                            <IconButton size="small" color="error" sx={{ mr: '10px' }} onClick={(e) => { e.stopPropagation(); handleDeleteEditPoint(point); }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  {/* Trailing line below last item */}
                  {sortedPoints.length > 0 && (
                    <Box sx={{ width: 32, height: 20, position: 'relative', flexShrink: 0 }}>
                      <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, bgcolor: '#e0e0e0', transform: 'translateX(-50%)' }} />
                    </Box>
                  )}
                </Box>
                {/* Top fade — outside scroll container so it stays fixed */}
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 80, background: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)', pointerEvents: 'none', opacity: topOpacity, transition: 'opacity 0.1s' }} />
                {/* Bottom fade */}
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 80, background: 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)', pointerEvents: 'none', opacity: bottomOpacity, transition: 'opacity 0.1s' }} />
              </Box>
            </Box>

            {/* Right: Form */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {selectedPoint && (
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2">
                    <Box component="span" sx={{ color: 'primary.main' }}>Version</Box>
                    {' | '}{selectedDate}
                  </Typography>
                </Box>
              )}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {selectedPoint ? (
                  <ShiftGroupFormFields
                    form={form}
                    errors={errors}
                    hideEditPointDate
                    lockGroupName
                    availableShifts={availableShifts}
                    onChange={setForm}
                  />
                ) : (
                  <Typography color="text.secondary">
                    Select a version or add a new one.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '15px' }}>
          <Button onClick={onClose}>Close</Button>
          {selectedPoint && (
            <Button onClick={handleSave} variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <Save />} disabled={saving}>
              Save
            </Button>
          )}
        </DialogActions>
        </FormNavigationProvider>
      </Dialog>

      <AddEditPointDialog
        open={addOpen}
        shiftGroupId={shiftGroupId}
        availableShifts={availableShifts}
        initialPayload={form}
        onClose={() => setAddOpen(false)}
        onAdded={(sg) => {
          onAdded(sg);
          setSelectedDate(sg.editPointDate);
          setAddOpen(false);
        }}
      />

      <DeleteEditPointDialog
        open={deleteOpen}
        editPoint={deleteTarget}
        onClose={() => setDeleteOpen(false)}
        onDeleted={handleDeleteDone}
      />
    </>
  );
}

// ─── ShiftGroups (Main Page) ──────────────────────────────────────────────────

export default function ShiftGroups() {
  const dispatch = useAppDispatch();
  const { shiftGroups, isLoading, error } = useAppSelector((state) => state.shiftGroups);
  const { shifts } = useAppSelector((state) => state.shifts);

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ShiftGroup | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [detailGroupId, setDetailGroupId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);


  // De-duplicate shifts: one entry per shiftId (latest editPointDate).
  const uniqueShiftOptions = React.useMemo(() => {
    const map = new Map<string, { shiftId: string; shiftName: string; editPointDate: string }>();
    for (const s of shifts) {
      const existing = map.get(s.shiftId);
      if (!existing || s.editPointDate > existing.editPointDate) {
        map.set(s.shiftId, { shiftId: s.shiftId, shiftName: s.shiftName, editPointDate: s.editPointDate });
      }
    }
    return Array.from(map.values())
      .map(({ shiftId, shiftName }) => ({ shiftId, shiftName }))
      .sort((a, b) => a.shiftName.localeCompare(b.shiftName));
  }, [shifts]);

  // De-duplicate shift groups: one row per shiftGroupId (latest editPointDate for display).
  const uniqueGroups = React.useMemo(() => {
    const map = new Map<string, ShiftGroup>();
    for (const g of shiftGroups) {
      const existing = map.get(g.shiftGroupId);
      if (!existing || g.editPointDate > existing.editPointDate) {
        map.set(g.shiftGroupId, g);
      }
    }
    return Array.from(map.values()).sort((a, b) => a.shiftGroupName.localeCompare(b.shiftGroupName));
  }, [shiftGroups]);

  const editPointsForGroup = useCallback(
    (shiftGroupId: string) => shiftGroups.filter((g) => g.shiftGroupId === shiftGroupId),
    [shiftGroups]
  );

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    dispatch(showNotification({ message, severity }));
  };

  return (
    <DashboardLayout>
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">Scheduling</Typography>
        <Typography variant="body2" color="text.primary" fontWeight={600}>Shift Groups</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Shift Groups</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => { dispatch(fetchShiftGroups()); dispatch(fetchShifts()); }}>
            <Refresh />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
          Create
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Shifts</TableCell>
                <TableCell>Latest Version</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uniqueGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary">No shift groups found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                uniqueGroups.map((g) => (
                  <TableRow key={g.shiftGroupId} hover>
                    <TableCell>{g.shiftGroupName}</TableCell>
                    <TableCell>{g.shiftIdList.length}</TableCell>
                    <TableCell>{g.editPointDate || '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDetailGroupId(g.shiftGroupId);
                            setDetailOpen(true);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete all versions">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleteTarget(g);
                            setDeleteOpen(true);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Dialog */}
      <CreateShiftGroupDialog
        open={createOpen}
        availableShifts={uniqueShiftOptions}
        onClose={() => setCreateOpen(false)}
        onCreated={(sg) => {
          dispatch(upsertShiftGroup(sg));
          showSnackbar(`Shift group "${sg.shiftGroupName}" created.`);
        }}
      />

      {/* Delete Dialog */}
      <DeleteShiftGroupDialog
        open={deleteOpen}
        group={deleteTarget}
        onClose={() => setDeleteOpen(false)}
        onDeleted={(id) => {
          dispatch(removeShiftGroupsByGroupId(id));
          showSnackbar('Shift group deleted.');
        }}
      />

      {/* Detail Dialog */}
      {detailGroupId && (
        <ShiftGroupDetailDialog
          open={detailOpen}
          shiftGroupId={detailGroupId}
          editPoints={editPointsForGroup(detailGroupId)}
          availableShifts={uniqueShiftOptions}
          onClose={() => {
            setDetailOpen(false);
            setDetailGroupId(null);
          }}
          onUpdated={(sg) => {
            dispatch(upsertShiftGroup(sg));
            showSnackbar('Version updated.');
          }}
          onAdded={(sg) => {
            dispatch(upsertShiftGroup(sg));
            showSnackbar('Version added.');
          }}
          onDeleted={(sgId, date) => {
            dispatch(removeShiftGroupEditPoint({ shiftGroupId: sgId, editPointDate: date }));
            showSnackbar('Version deleted.');
          }}
        />
      )}

    </Box>
    </DashboardLayout>
  );
}
